import { HttpErrorResponse } from '@angular/common/http';
import { ProblemDetail } from '../interfaces/error/IProblemDetail';

export const PROBLEM_TYPE_PREFIX = 'https://don.ilionx.nl/problems/';
export const PROBLEM_TYPE_UNKNOWN = 'about:blank';

const PROBLEM_SLUG_BY_STATUS: Readonly<Record<number, string | undefined>> = {
  0: 'network-unavailable',
  400: 'validation-failed',
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'not-found',
  409: 'conflict',
  422: 'unprocessable-entity',
  504: 'gateway-timeout'
};

const TITLE_BY_STATUS: Readonly<Record<number, string | undefined>> = {
  0: 'Geen verbinding',
  400: 'Ongeldige invoer',
  401: 'Niet ingelogd',
  403: 'Geen toegang',
  404: 'Niet gevonden',
  409: 'Conflict',
  422: 'Aanvraag geweigerd',
  504: 'Verbinding geblokkeerd'
};

const SERVER_ERROR_TITLE = 'Er is iets misgegaan';
const UNKNOWN_TITLE = 'Onbekende fout';
const NETWORK_DETAIL = 'Er kon geen verbinding met SkillCentral worden gemaakt.';
const SERVER_DETAIL = 'Er is iets misgegaan. Probeer het later opnieuw.';
const UNKNOWN_DETAIL = 'Er is een onbekende fout opgetreden.';

/**
 * Typed, status-aware view of a failed HTTP call.
 *
 * `detail` is safe to render — it is either the backend's own message or a
 * generic Dutch fallback. `response` is for logging only and must never reach
 * the UI.
 */
export class DonError extends Error implements ProblemDetail {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string;
  readonly instance: string | null;
  readonly errors: Readonly<Record<string, string>>;
  readonly response: HttpErrorResponse;

  private constructor(problem: ProblemDetail, response: HttpErrorResponse) {
    super(problem.detail);
    this.name = 'DonError';
    this.type = problem.type;
    this.title = problem.title;
    this.status = problem.status;
    this.detail = problem.detail;
    this.instance = problem.instance;
    this.errors = problem.errors;
    this.response = response;
  }

  static fromHttpError(response: HttpErrorResponse): DonError {
    return new DonError(toProblemDetail(response), response);
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isValidationError(): boolean {
    return this.status === 400;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnprocessable(): boolean {
    return this.status === 422;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }

  hasFieldErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  fieldError(field: string): string | undefined {
    return Object.prototype.hasOwnProperty.call(this.errors, field) ? this.errors[field] : undefined;
  }
}

export function isDonError(value: unknown): value is DonError {
  return value instanceof DonError;
}

function toProblemDetail(response: HttpErrorResponse): ProblemDetail {
  const body = parseBody(response.error);
  const status = readNumber(body, 'status') ?? response.status;

  return {
    type: readString(body, 'type') ?? problemTypeFor(status),
    title: readString(body, 'title') ?? titleFor(status),
    status,
    // `detail` is RFC 7807; `message` is what don-backend sends today.
    detail: readString(body, 'detail') ?? readString(body, 'message') ?? detailFor(status),
    instance: readString(body, 'instance'),
    errors: readErrors(body)
  };
}

function problemTypeFor(status: number): string {
  const slug = PROBLEM_SLUG_BY_STATUS[status];
  if (slug !== undefined) {
    return `${PROBLEM_TYPE_PREFIX}${slug}`;
  }
  return status >= 500 ? `${PROBLEM_TYPE_PREFIX}internal-error` : PROBLEM_TYPE_UNKNOWN;
}

function titleFor(status: number): string {
  const title = TITLE_BY_STATUS[status];
  if (title !== undefined) {
    return title;
  }
  return status >= 500 ? SERVER_ERROR_TITLE : UNKNOWN_TITLE;
}

function detailFor(status: number): string {
  if (status === 0) {
    return NETWORK_DETAIL;
  }
  return status >= 500 ? SERVER_DETAIL : UNKNOWN_DETAIL;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Angular hands back a parsed object, a raw string, or a ProgressEvent. */
function parseBody(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return isRecord(raw) ? raw : null;
}

function readString(body: Record<string, unknown> | null, key: string): string | null {
  if (body === null) {
    return null;
  }
  const value = body[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function readNumber(body: Record<string, unknown> | null, key: string): number | null {
  if (body === null) {
    return null;
  }
  const value = body[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readErrors(body: Record<string, unknown> | null): Readonly<Record<string, string>> {
  if (body === null) {
    return {};
  }
  const raw = body['errors'];
  if (!isRecord(raw)) {
    return {};
  }
  const errors: Record<string, string> = {};
  for (const [field, message] of Object.entries(raw)) {
    if (typeof message === 'string') {
      errors[field] = message;
    }
  }
  return errors;
}
